import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AmplitudeFlagKey,
  AmplitudeVariantValue as AmplitudeVariantValueCommon,
  CampaignPaymentTab,
  EmailContentType,
  FormCategory,
  FormType,
  getDonationOrTicketingFormLink,
  OrganizationRoutes,
} from "@simplyk/common";
import { useRouter } from "next/router";

import { EmbedChoice } from "./enums";

import { AmplitudeEvents } from "@/constants/amplitude";
import { useCurrentUserContext } from "@/contexts/CurrentUserContext";
import { useLocaleContext } from "@/contexts/LocaleContext";
import { trpc } from "@/helpers/trpc";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useAmplitudeFeatureFlag } from "@/hooks/amplitude/useAmplitudeFeatureFlag";
import { parseAsBoolean, useQueryState } from "@/hooks/useQueryState";
import { useUrlBuilder } from "@/hooks/useUrlBuilder";
import { FRONTEND_URL } from "@/routes";
import { AmplitudeVariantValue } from "@/src/gql/gql-types";
import { useUpdateOrganizationFlagsMutation } from "@/src/gql/queries/generated/organizationQuery";

export type ShareFormModalTab = RootLevelShareFormModalTab | `Embed${EmbedChoice}Instructions`;

export type RootLevelShareFormModalTab = "Home" | "QrCode" | "ChooseEmbedType";

type UseConnectedShareFormModalStateProps = {
  formId: string;
  formType: FormType;
  defaultTab: ShareFormModalTab;
};

export const useConnectedShareFormModalState = ({
  formId,
  formType,
  defaultTab,
}: UseConnectedShareFormModalStateProps) => {
  const { logAmplitudeEvent } = useAmplitude();
  const buildUrl = useUrlBuilder();
  const { isoLocale } = useLocaleContext();
  const router = useRouter();

  const [isFormCreation] = useQueryState("isFormCreation", parseAsBoolean);
  const [isGivingTuesday] = useQueryState("givingTuesdayTemplate", parseAsBoolean);
  const [tab, setTab] = useState<ShareFormModalTab>(defaultTab);
  const [savedEmbedChoice, setSavedEmbedChoice] = useState<EmbedChoice | undefined>();

  const { organization, isOrganization } = useCurrentUserContext();
  const isOnboarding = !Boolean(organization?.fourthPaymentDate);
  const organizationId = organization?.id;

  const defaultCollapseAdditionalSharingOptions = isOnboarding ? defaultTab === "Home" : false;
  const [savedCollapseAdditionalSharingOptions, setSavedCollapseAdditionalSharingOptions] = useState(
    defaultCollapseAdditionalSharingOptions
  );

  const [updateOrganizationFlags] = useUpdateOrganizationFlagsMutation();

  const {
    data: shareFormDetails,
    isLoading: isShareFormDetailsLoading,
    error: shareFormDetailsError,
  } = trpc.getShareFormDetails.useQuery({
    formId,
    formType,
  });

  const canShowInvitationEmailOption = !isOnboarding && !shareFormDetails?.isClosed;

  const {
    data: communicationEmailStatus,
    isLoading: isCommunicationEmailStatusLoading,
    error: communicationEmailStatusError,
  } = trpc.getCommunicationEmailStatus.useQuery(
    {
      formId,
    },
    { enabled: canShowInvitationEmailOption }
  );

  const hasSentInvitation =
    communicationEmailStatus?.items?.some(
      (item) => item.emailType === EmailContentType.INVITATION && item.status === "sent"
    ) ?? true;

  const {
    isActive: isDemotivateEmbedsEnabled,
    loading: isLoadingDemotivateEmbedsFeatureFlag,
    value,
  } = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.DemotivateEmbeds,
    organizationId,
    skip: !!organization?.demotivateEmbeds,
  });
  const shouldDemotivateEmbeds = Boolean(
    isDemotivateEmbedsEnabled || organization?.demotivateEmbeds === AmplitudeVariantValueCommon.B
  );

  useEffect(() => {
    if (isOrganization && !isLoadingDemotivateEmbedsFeatureFlag && !organization?.demotivateEmbeds && value) {
      updateOrganizationFlags({
        variables: { input: { demotivateEmbeds: value.toUpperCase() as AmplitudeVariantValue } },
      });
    }
  }, [
    isOrganization,
    isLoadingDemotivateEmbedsFeatureFlag,
    organization?.demotivateEmbeds,
    updateOrganizationFlags,
    value,
  ]);

  const availableEmbedChoices = useMemo(() => {
    return [
      shouldDemotivateEmbeds && "LinkedButton",
      "ButtonPopup",
      "Form",
      shareFormDetails?.hasThermometer && "Thermometer",
      shareFormDetails?.campaignId && "Leaderboard",
    ].filter(Boolean) as EmbedChoice[];
  }, [shouldDemotivateEmbeds, shareFormDetails?.hasThermometer, shareFormDetails?.campaignId]);

  const formLink = shareFormDetails
    ? `${FRONTEND_URL}${getDonationOrTicketingFormLink({
        form: {
          id: formId,
          path: shareFormDetails.path,
          formType,
          formCategory: FormCategory[shareFormDetails.formCategory],
          isPrimaryCampaign: shareFormDetails.isPrimaryCampaign,
          isPrimaryTeam: shareFormDetails.isPrimaryTeam,
        },
        isoLocale,
      })}`
    : undefined;

  return {
    isLoading:
      isShareFormDetailsLoading ||
      (canShowInvitationEmailOption && isCommunicationEmailStatusLoading) ||
      isLoadingDemotivateEmbedsFeatureFlag,
    hasError: shareFormDetailsError || communicationEmailStatusError,
    isOnboarding,
    isFormCreation,
    isGivingTuesday,
    formLink,
    formCategory: shareFormDetails?.formCategory,
    formPath: shareFormDetails?.path,
    formIsPrimaryCampaign: shareFormDetails?.isPrimaryCampaign,
    formIsPrimaryTeam: shareFormDetails?.isPrimaryTeam,
    currentTab: tab,
    canCollapseAdditionalSharingOptions: isOnboarding,
    defaultCollapseAdditionalSharingOptions: savedCollapseAdditionalSharingOptions,
    defaultEmbedChoice: savedEmbedChoice,
    showTitleByFormCategory: isOnboarding,
    availableEmbedChoices,
    shouldShowInvitationEmailAction:
      canShowInvitationEmailOption && !isCommunicationEmailStatusLoading && !hasSentInvitation,
    onGoBackHome: useCallback(() => {
      setTab("Home");
      setSavedEmbedChoice(undefined);
      logAmplitudeEvent(AmplitudeEvents.ShareFormModalClickBackBase);
    }, [logAmplitudeEvent]),
    onGoToQrCode: useCallback(() => {
      setTab("QrCode");
    }, []),
    onGoToChooseEmbedType: useCallback(() => {
      setTab("ChooseEmbedType");
    }, []),
    onExpandAdditionalSharingOptions: useCallback(() => {
      setSavedCollapseAdditionalSharingOptions(false);
    }, []),
    onCollapseAdditionalSharingOptions: useCallback(() => {
      setSavedCollapseAdditionalSharingOptions(true);
    }, []),
    onChooseEmbedType: useCallback(
      (choice: EmbedChoice) => {
        setSavedEmbedChoice(choice);
        logAmplitudeEvent(AmplitudeEvents.ShareFormModalClickChoose, { tab: choice });
        setTab(`Embed${choice}Instructions`);
      },
      [logAmplitudeEvent]
    ),
    onInvitationEmailActionClick: useCallback(() => {
      logAmplitudeEvent(AmplitudeEvents.FormSharingSendInvitationClicked, {
        ticketingId: formId,
      });

      router.push(
        buildUrl({
          route: OrganizationRoutes.CampaignHub,
          searchParams: {
            formId,
            tab: CampaignPaymentTab.Communication,
            openInvitationEmail: "true",
          },
        })
      );
    }, [buildUrl, formId, logAmplitudeEvent, router]),
    reset: useCallback(() => {
      setTab(defaultTab);
      setSavedEmbedChoice(undefined);
      setSavedCollapseAdditionalSharingOptions(defaultCollapseAdditionalSharingOptions);
    }, [defaultCollapseAdditionalSharingOptions, defaultTab]),
  };
};
